import ConversationAnswer from "../../molecules/conversation-answer";
import React, {ReactNode, useEffect, useRef, useState} from "react";
import MessageType from "../../../lib/MessageType";
import './index.scss';
import BtnChevron, {ChevronDirection} from "../../../../../../../../design/1/js/templates/atoms/btn-chevron";
import {MessageToolView} from "../../../lib/enum/MessageToolView";
import AnswerType from "../../../lib/AnswerType";
import JsonResponse from "../../../../../../../../design/1/js/lib/entity/response/JsonResponse";
import {toast} from "react-hot-toast";
import formatErrorToastMessage from "../../../../../../../../design/1/js/lib/formatErrorToastMessage";
import {MessageStatus} from "../../../lib/enum/MessageStatus";
import NewMessageEvent from "../../../../../../../../design/1/js/lib/types/NewMessageEvent";

interface MessagetoolConversationProps {
    message: MessageType,
    messageAction: string;
    switchView: (view: MessageToolView) => void;
    updateReadStatusAction: string;
    getAttachmentAction: string;
    answerForm?: (addAnswer: (answer: AnswerType) => void) => ReactNode;
    isClient?: boolean;
}

const MessagetoolConversation = ({
    message, messageAction, switchView, updateReadStatusAction, getAttachmentAction, answerForm, isClient,
}: MessagetoolConversationProps) => {
    const baseClassName = 'm-baseConversation';
    const bottomRef = useRef(null);
    const [messageAnswers, setMessageAnswers] = useState([]);

    useEffect(() => {
        scrollToBottom();
        modifyBaseStyles();
        loadNewMessage();
        markAllAsRead();

        // Revert modifications on dismount
        return () => {
            revertStyleModifications();
        }
    }, []);

    const loadNewMessage = () => {
        fetch(messageAction +'?id=' + message.id)
            .then(res => res.json())
            .then((response: JsonResponse) => {
                setMessageAnswers(response.context.answers);
                scrollToBottom();
            });
    }

    useEffect(() => {
        const newMessageCallback = (event: NewMessageEvent) => {
            if (event.detail.inboxMetadata.id !== message.id) {
                return;
            }

            const answer: AnswerType = {
                id: event.detail.id,
                fromMPK: event.detail.author === 'mpk',
                text: event.detail.content,
                read: event.detail.isRead,
                date: event.detail.createdAt,
                sender: event.detail.sender,
                attachments: event.detail.attachments,
            };
            addAnswer(answer);

            if (answer.fromMPK !== isClient) {
                return;
            }
            markMessageAsReadIfNotHidden(message.id, event.detail.id);
        }
        document.addEventListener('inbox_new_message', newMessageCallback);

        return () => {
            document.removeEventListener('inbox_new_message', newMessageCallback);
        }
    }, [message]);


    const markMessageAsReadIfNotHidden = (inboxId: number, messageId: number) => {
        setTimeout(() => {
            if (!document.hidden) {
                markMessagesAsRead(inboxId, [messageId]);
            } else {
                document.addEventListener('visibilitychange', () => {
                    markMessageAsReadIfNotHidden(inboxId, messageId);
                }, { once: true });
            }
        }, 3000);
    }

    const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: 'smooth' });

    const modifyBaseStyles = () => {
        const widgetContainer = document.getElementsByClassName('sv-slot-content')[0] as HTMLDivElement;
        widgetContainer.style.paddingTop = 'var(--spacer-xs)';

        const outerWidgetContainer = document.getElementsByClassName('plf-default-content')[0] as HTMLDivElement;
        outerWidgetContainer.style.width = '100%';

        document.body.classList.add('messageToolConversationView');
    }

    const revertStyleModifications = () => {
        const widgetContainer = document.getElementsByClassName('sv-slot-content')[0] as HTMLDivElement;
        const outerWidgetContainer = document.getElementsByClassName('plf-default-content')[0] as HTMLDivElement;

        widgetContainer.style.removeProperty('padding-top');
        outerWidgetContainer.style.removeProperty('width');

        document.body.classList.remove('messageToolConversationView');
    }

    const returnToInbox = () => {
        switchView(MessageToolView.INBOX);
    }

    const addAnswer = (answer: AnswerType) => {
        setMessageAnswers((previousMessages) => {
            if (previousMessages.some((message) => message.id === answer.id)) {
                return previousMessages;
            }

            return [...previousMessages, answer];
        });
    };

    const formElement = answerForm ? answerForm(addAnswer) : null;

    const markMessagesAsRead = (inboxId: number, messageIds: number[]) => {
        fetch(updateReadStatusAction, {
            method: 'POST',
            body: JSON.stringify({ inboxId: inboxId, messageIds: messageIds }),
            headers: {
                'Content-type': 'application/json; charset=UTF-8',
                'X-Csrf-Token': document.body.dataset.csrfToken,
            },
        }).then(res => res.json())
            .then((response: JsonResponse) => {
                if (!response.success) {
                    toast.error(formatErrorToastMessage(window.sv_resource.get('plf_error_unknown'), response.traceId))
                }
            });
    }

    const markAllAsRead = () => {
        const unreadAnswersFromOther = message.answers.filter((answer) => {
            return answer.fromMPK === isClient && !answer.read;
        });
        if (unreadAnswersFromOther.length === 0) {
            return;
        }
        markMessagesAsRead(message.id, unreadAnswersFromOther.map((answer) => answer.id));
    }

    let clientInfo = null;

    if (!isClient) {
        const { clientName, clientVersIntId, clientVersId } = message.client;
        const versIntIdLabel = window.sv_resource.get('plf_messagetool_consultant_client_info_versintid');
        const versNrLabel = window.sv_resource.get('plf_messagetool_consultant_client_info_versnr');
        clientInfo = `${clientName} - ${versIntIdLabel}: ${clientVersIntId} - ${versNrLabel} ${clientVersId}`;
    }

    const archivedText =
        message.status === MessageStatus.Done
            ? ` — ${window.sv_resource.get('plf_messagetool_client_archived')}`
            : '';

    return (
        <>
            <div className={`${baseClassName}__overlay`}>
                <div className={`${baseClassName}__conversationNav`} onClick={returnToInbox}>
                    <BtnChevron className={`${baseClassName}__chevron`} direction={ChevronDirection.Left} standalone />
                    <div className={`${baseClassName}__conversationInfo`}>
                        <div className={`${baseClassName}__conversationTitle`}>
                            {message.title + archivedText}
                        </div>
                        {!isClient && (
                            <div>
                                {clientInfo}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className={`${baseClassName}__panel`}>
                {
                    messageAnswers.map((answer) => {
                        return (
                            <ConversationAnswer
                                key={`answer-${answer.id}`}
                                answer={answer}
                                inboxId={message.id}
                                isClient={isClient}
                                getAttachmentAction={getAttachmentAction}
                            />
                        );
                    })
                }
            </div>
            {formElement}
            <div ref={bottomRef}></div>
        </>
    );
}

MessagetoolConversation.defaultProps = {
    isClient: false,
}

export default MessagetoolConversation;
